
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Mail, MapPin } from 'lucide-react';

const sections = [
  { label: "About", id: "about" },
  { label: "Products", id: "products" },
  { label: "Industries", id: "industries" },
  { label: "Testimonials", id: "testimonials" },
  { label: "Contact", id: "contact" },
];

const Layout = ({ children }: { children: React.ReactNode }) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToSection = (id: string) => {
    if (location.pathname !== "/") {
      navigate("/", { replace: false });
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 200);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const scrollToTop = () => {
    if (location.pathname !== "/") {
      navigate("/");
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
    setIsMenuOpen(false);
  };

  // New function to handle footer navigation with scroll to top
  const handleFooterNavigation = (path: string) => {
    navigate(path);
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 100);
  };

  const toggleMenu = () => setIsMenuOpen((v) => !v);

  return (
    <div className="min-h-screen flex flex-col">
      <div className="bg-tech-dark text-white py-2 text-sm hidden md:block">
        <div className="container mx-auto flex justify-between items-center container-padding">
          <div className="flex space-x-6">
            <a href="tel:02269647168" className="flex items-center space-x-1 hover:text-accent transition-colors">
              <Phone size={14} />
              <span>022 6964 7168</span>
            </a>
            <a href="mailto:support@truecoregadgets.com" className="flex items-center space-x-1 hover:text-accent transition-colors">
              <Mail size={14} />
              <span>support@truecoregadgets.com</span>
            </a>
          </div>
          <div className="flex space-x-6">
            <span className="flex items-center space-x-1">
              <MapPin size={14} />
              <span>Mumbai | Delhi | Hyderabad</span>
            </span>
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border/50">
        <div className="container mx-auto flex justify-between items-center h-16 container-padding">
          <button 
            onClick={scrollToTop} 
            className="flex items-center space-x-2 bg-transparent border-0 cursor-pointer"
          >
            <span className="text-2xl font-bold gradient-text">TRUECORE</span>
            <span className="hidden md:inline-block text-muted-foreground">Gadget Innovations</span>
          </button>

          <nav className="hidden md:flex items-center space-x-8">
            <button
              type="button"
              onClick={scrollToTop}
              className="text-foreground hover:text-primary transition-colors bg-transparent border-none p-0 m-0 cursor-pointer"
            >
              Home
            </button>
            <Link
              to="/about-truecore-gadgets"
              className="text-foreground hover:text-primary transition-colors"
            >
              About
            </Link>
            <div className="relative group">
              <button type="button" className="flex items-center space-x-1 text-foreground hover:text-primary transition-colors bg-transparent border-none p-0 m-0 cursor-pointer">
                <span>Products</span>
                <ChevronDown size={16} />
              </button>
              <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-card border border-border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-40">
                <div className="py-1">
                  <Link to="/products" className="block px-4 py-2 text-sm text-foreground hover:bg-muted/50 w-full text-left">All Products</Link>
                  <Link to="/products/tablets" className="block px-4 py-2 text-sm text-foreground hover:bg-muted/50 w-full text-left">Custom Tablets</Link>
                  <Link to="/products/pcs" className="block px-4 py-2 text-sm text-foreground hover:bg-muted/50 w-full text-left">Custom PCs</Link>
                  <Link to="/products/smart-gadgets" className="block px-4 py-2 text-sm text-foreground hover:bg-muted/50 w-full text-left">Smart Gadgets</Link>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => scrollToSection("industries")}
              className="text-foreground hover:text-primary transition-colors bg-transparent border-none p-0 m-0 cursor-pointer"
            >
              Industries
            </button>
            <button
              type="button"
              onClick={() => scrollToSection("testimonials")}
              className="text-foreground hover:text-primary transition-colors bg-transparent border-none p-0 m-0 cursor-pointer"
            >
              Testimonials
            </button>
            <Link
              to="/contact-truecore"
              className="text-foreground hover:text-primary transition-colors"
            >
              Contact
            </Link>
          </nav>

          <div className="hidden md:block">
            <Link
              to="/contact-truecore"
              className="tech-btn-primary cursor-pointer"
            >
              Enquire Now
            </Link>
          </div>

          <button className="md:hidden text-foreground" onClick={toggleMenu}>
            {isMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {isMenuOpen && (
          <div className="md:hidden bg-background border-t border-border/50">
            <div className="container mx-auto py-4 space-y-3 container-padding">
              <Link to="/" className="block text-foreground hover:text-primary transition-colors" onClick={toggleMenu}>Home</Link>
              <Link to="/about-truecore-gadgets" className="block text-foreground hover:text-primary transition-colors" onClick={toggleMenu}>About</Link>
              <div className="block text-foreground font-medium pt-2">Products</div>
              <div className="pl-4 space-y-2">
                <Link to="/products" className="block text-foreground hover:text-primary transition-colors" onClick={toggleMenu}>All Products</Link>
                <Link to="/products/tablets" className="block text-foreground hover:text-primary transition-colors" onClick={toggleMenu}>Custom Tablets</Link>
                <Link to="/products/pcs" className="block text-foreground hover:text-primary transition-colors" onClick={toggleMenu}>Custom PCs</Link>
                <Link to="/products/smart-gadgets" className="block text-foreground hover:text-primary transition-colors" onClick={toggleMenu}>Smart Gadgets</Link>
              </div>
              <button type="button" className="block text-foreground hover:text-primary transition-colors bg-transparent border-none w-full text-left cursor-pointer" onClick={() => {scrollToSection("industries")}}>Industries</button>
              <button type="button" className="block text-foreground hover:text-primary transition-colors bg-transparent border-none w-full text-left cursor-pointer" onClick={() => {scrollToSection("testimonials")}}>Testimonials</button>
              <Link to="/contact-truecore" className="block text-foreground hover:text-primary transition-colors" onClick={toggleMenu}>Contact</Link>
              <div className="pt-2">
                <Link to="/contact-truecore" className="tech-btn-primary block text-center w-full cursor-pointer" onClick={toggleMenu}>
                  Enquire Now
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        {children}
      </main>

      <footer className="bg-tech-dark text-white">
        <div className="container mx-auto section-padding container-padding">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Truecore Gadget Innovations</h3>
              <p className="text-gray-300 mb-4">25 years of building tech that works for you. Industry-leading custom gadget solutions for individuals and businesses.</p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-300 hover:text-accent">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-facebook"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
                </a>
                <a href="#" className="text-gray-300 hover:text-accent">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-twitter"><path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"/></svg>
                </a>
                <a href="#" className="text-gray-300 hover:text-accent">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-linkedin"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect width="4" height="12" x="2" y="9"/><circle cx="4" cy="4" r="2"/></svg>
                </a>
                <a href="#" className="text-gray-300 hover:text-accent">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-instagram"><rect width="20" height="20" x="2" y="2" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/></svg>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><button type="button" onClick={scrollToTop} className="text-gray-300 hover:text-accent transition-colors bg-transparent border-none cursor-pointer">Home</button></li>
                <li><button type="button" onClick={() => handleFooterNavigation("/about-truecore-gadgets")} className="text-gray-300 hover:text-accent transition-colors bg-transparent border-none cursor-pointer">About Us</button></li>
                <li><button type="button" onClick={() => handleFooterNavigation("/products")} className="text-gray-300 hover:text-accent transition-colors bg-transparent border-none cursor-pointer">Products</button></li>
                <li><button type="button" onClick={() => scrollToSection("industries")} className="text-gray-300 hover:text-accent transition-colors bg-transparent border-none cursor-pointer">Industries</button></li>
                <li><button type="button" onClick={() => handleFooterNavigation("/blog")} className="text-gray-300 hover:text-accent transition-colors bg-transparent border-none cursor-pointer">Blog</button></li>
                <li><button type="button" onClick={() => handleFooterNavigation("/contact-truecore")} className="text-gray-300 hover:text-accent transition-colors bg-transparent border-none cursor-pointer">Contact</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">Products & Support</h4>
              <ul className="space-y-2">
                <li><button type="button" onClick={() => handleFooterNavigation("/products/tablets")} className="text-gray-300 hover:text-accent transition-colors bg-transparent border-none cursor-pointer">Custom Tablets</button></li>
                <li><button type="button" onClick={() => handleFooterNavigation("/products/pcs")} className="text-gray-300 hover:text-accent transition-colors bg-transparent border-none cursor-pointer">Custom PCs</button></li>
                <li><button type="button" onClick={() => handleFooterNavigation("/products/smart-gadgets")} className="text-gray-300 hover:text-accent transition-colors bg-transparent border-none cursor-pointer">Smart Gadgets</button></li>
                <li><button type="button" onClick={() => handleFooterNavigation("/technical-specifications")} className="text-gray-300 hover:text-accent transition-colors bg-transparent border-none cursor-pointer">Technical Specifications</button></li>
                <li><button type="button" onClick={() => handleFooterNavigation("/warranty-policy")} className="text-gray-300 hover:text-accent transition-colors bg-transparent border-none cursor-pointer">Warranty</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">Contact Us</h4>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <MapPin className="text-accent mt-1" size={18} />
                  <span className="text-gray-300">
                    Mumbai Office: Tech Park, Andheri East, Mumbai - 400069
                  </span>
                </li>
                <li className="flex items-center space-x-3">
                  <Phone className="text-accent" size={18} />
                  <span className="text-gray-300">022 6964 7168</span>
                </li>
                <li className="flex items-center space-x-3">
                  <Mail className="text-accent" size={18} />
                  <span className="text-gray-300">support@truecoregadgets.com</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm">© 2025 Truecore Gadget Innovations. All rights reserved.</p>
              <div className="flex space-x-6 mt-4 md:mt-0">
                <button type="button" onClick={() => handleFooterNavigation("/privacy-policy")} className="text-gray-400 hover:text-accent text-sm transition-colors bg-transparent border-none cursor-pointer">Privacy Policy</button>
                <button type="button" onClick={() => handleFooterNavigation("/terms-and-conditions")} className="text-gray-400 hover:text-accent text-sm transition-colors bg-transparent border-none cursor-pointer">Terms & Conditions</button>
                <button type="button" onClick={() => handleFooterNavigation("/shipping-policy")} className="text-gray-400 hover:text-accent text-sm transition-colors bg-transparent border-none cursor-pointer">Shipping Policy</button>
                <button type="button" onClick={() => handleFooterNavigation("/refund-policy")} className="text-gray-400 hover:text-accent text-sm transition-colors bg-transparent border-none cursor-pointer">Refund Policy</button>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
